import {
    createContext,
    type ReactNode,
    type RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

const STORAGE_KEY = "carzelle-sidebar-collapsed";

interface SidebarContextValue {
    isCollapsed: boolean;
    toggle: () => void;
    open: () => void;
    close: () => void;
    /**
     * The mobile drawer. It lives here rather than inside `Sidebar` because
     * the button that opens it sits in the app header, so that it shares a
     * centre line with the title and the header actions.
     */
    mobileOpen: boolean;
    setMobileOpen: (open: boolean) => void;
    /**
     * That header button. The drawer has no Radix trigger of its own to
     * return focus to on close, so it hands focus back here instead of
     * letting it fall to `<body>`.
     */
    menuButtonRef: RefObject<HTMLButtonElement | null>;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

interface SidebarProviderProps {
    children: ReactNode;
    storageKey?: string;
}

export function SidebarProvider({ children, storageKey = STORAGE_KEY }: SidebarProviderProps) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const menuButtonRef = useRef<HTMLButtonElement | null>(null);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        if (typeof window === "undefined") return true;
        const stored = localStorage.getItem(storageKey);
        return stored === null ? true : stored === "true";
    });

    useEffect(() => {
        if (typeof window === "undefined") return;
        const stored = localStorage.getItem(storageKey);
        setIsCollapsed(stored === null ? true : stored === "true");
    }, [storageKey]);

    const updateState = useCallback(
        (value: boolean | ((prev: boolean) => boolean)) => {
            setIsCollapsed((prev) => {
                const next = typeof value === "function" ? (value as (prev: boolean) => boolean)(prev) : value;
                if (typeof window !== "undefined") {
                    localStorage.setItem(storageKey, String(next));
                }
                return next;
            });
        },
        [storageKey]
    );

    const toggle = useCallback(() => updateState((prev) => !prev), [updateState]);
    const open = useCallback(() => updateState(false), [updateState]);
    const close = useCallback(() => updateState(true), [updateState]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const handler = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === "b") {
                event.preventDefault();
                toggle();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [toggle]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const handler = (event: StorageEvent) => {
            if (event.key === storageKey && typeof event.newValue === "string") {
                setIsCollapsed(event.newValue === "true");
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, [storageKey]);

    const value = useMemo(
        () => ({ isCollapsed, toggle, open, close, mobileOpen, setMobileOpen, menuButtonRef }),
        [isCollapsed, toggle, open, close, mobileOpen]
    );

    return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error("useSidebar must be used within a SidebarProvider");
    }
    return context;
}
